import { Buffer } from 'buffer';
import { RawDraftContentState } from 'draft-js';
import { DataTableSortContent, DataTableSortField } from '@/components/element/types';
const prefix = 'node_';

const keys = {
  sales_order_comment: `${prefix}sales_order_comment`,
  purchase_order_comment: `${prefix}purchase_order_comment`,
  invoice_comment: `${prefix}invoice_comment`,
  payment_comment: `${prefix}payment_comment`,
  purchase_order_contract_comment: `${prefix}purchase_order_contract_comment`,
  quotation_comment: `${prefix}quotation_comment`,
  sales_order_contract_comment: `${prefix}sales_order_contract_comment`,
  latest_notification_id: `${prefix}latest_notification_id`,
  sales_order_data_table: `${prefix}sales_order_data_table`,
  purchase_order_data_table: `${prefix}purchase_order_data_table`,
  invoice_data_table: `${prefix}invoice_data_table`,
  payment_data_table: `${prefix}payment_data_table`,
  purchase_order_contract_data_table: `${prefix}purchase_order_contract_data_table`,
  quotation_data_table: `${prefix}quotation_data_table`,
  sales_order_contract_data_table: `${prefix}sales_order_contract_data_table`,
  approval_data_table: `${prefix}approval_data_table`,
  weekly_comparison_data_table: `${prefix}weekly_comparison_data_table`,
  fixed_assets_data_table: `${prefix}fixed_assets_data_table_data_table`,
  selected_plan_id: `${prefix}selected_plan_id`, // プラン変更時にStripeで画面リダイレクトされる際に値を保持するために使用
};

const StorageUtil = {
  set: (key: string, val: string) => sessionStorage.setItem(key, val),
  get: (key: string) => sessionStorage.getItem(key),
  clear: (key: string) => sessionStorage.removeItem(key),
  setAsBase64: (key: string, val: string) => {
    const data = Buffer.from(val).toString('base64');
    sessionStorage.setItem(key, data);
  },
  getAsBase64: (key: string) => {
    const data = sessionStorage.getItem(key) as string;
    if (!data) {
      return undefined;
    }
    return Buffer.from(data, 'base64').toString();
  },
};

const identifierToCommentKey = (identifier: string) => {
  let key = null;
  if (identifier.startsWith('sales_order_')) key = keys.sales_order_comment;
  if (identifier.startsWith('purchase_order_')) key = keys.purchase_order_comment;
  if (identifier.startsWith('invoice_')) key = keys.invoice_comment;
  if (identifier.startsWith('payment_')) key = keys.payment_comment;
  if (identifier.startsWith('purchase_order_contract_')) key = keys.purchase_order_contract_comment;
  if (identifier.startsWith('quotation_')) key = keys.quotation_comment;
  if (identifier.startsWith('sales_order_contract_')) key = keys.sales_order_contract_comment;
  return key;
};

const identifierToDataTableKey = (identifier: string) => {
  let key = null;
  if (identifier.startsWith('sales_order_')) key = keys.sales_order_data_table;
  if (identifier.startsWith('purchase_order_')) key = keys.purchase_order_data_table;
  if (identifier.startsWith('invoice_')) key = keys.invoice_data_table;
  if (identifier.startsWith('payment_')) key = keys.payment_data_table;
  if (identifier.startsWith('purchase_order_contract_')) key = keys.purchase_order_contract_data_table;
  if (identifier.startsWith('quotation_')) key = keys.quotation_data_table;
  if (identifier.startsWith('sales_order_contract_')) key = keys.sales_order_contract_data_table;
  if (identifier.startsWith('approval_')) key = keys.approval_data_table;
  if (identifier.startsWith('weekly_comparison_')) key = keys.weekly_comparison_data_table;
  if (identifier.startsWith('fixed_assets_')) key = keys.fixed_assets_data_table;
  return key;
};

export const storage = {
  comment: {
    set: (identifier: string, content: RawDraftContentState) => {
      try {
        const key = identifierToCommentKey(identifier);
        if (key) {
          let comments = StorageUtil.getAsBase64(key);
          if (comments) {
            let commentsAry = JSON.parse(comments).filter((e: any) => e['identifier'] != identifier);
            StorageUtil.setAsBase64(key, JSON.stringify([{ identifier, content }, ...commentsAry.slice(0, 14)]));
          } else {
            StorageUtil.setAsBase64(key, JSON.stringify([{ identifier, content }]));
          }
        }
      } catch (e) {
        return undefined;
      }
    },
    get: (_identifier: string): RawDraftContentState | undefined => {
      try {
        const key = identifierToCommentKey(_identifier);
        if (key == null) return undefined;

        const comments = StorageUtil.getAsBase64(key);
        if (comments) {
          let comment = JSON.parse(comments).find((e: any) => e['identifier'] == _identifier);
          if (comment) {
            return comment['content'];
          }
        }
        return undefined;
      } catch (e) {
        return undefined;
      }
    },
  },
  notification: {
    setLatestId: (id: number) => {
      StorageUtil.set(keys.latest_notification_id, `${id}`);
    },
    getLatestId: () => {
      const id = StorageUtil.get(keys.latest_notification_id);
      return id != null ? Number(id) : null;
    },
  },
  dataTableSortContent: {
    set: (identifier: string, content: DataTableSortContent) => {
      try {
        const key = identifierToDataTableKey(identifier);
        if (key) {
          let sortContents = StorageUtil.getAsBase64(key);
          if (sortContents) {
            let contentsAry = JSON.parse(sortContents).filter((e: any) => e['identifier'] != identifier);
            if (content.dataTableSortField || content.dataTableSearchKeyword) {
              StorageUtil.setAsBase64(key, JSON.stringify([{ identifier, content }, ...contentsAry]));
            } else {
              StorageUtil.setAsBase64(key, JSON.stringify(contentsAry));
            }
          } else {
            if (content) {
              StorageUtil.setAsBase64(key, JSON.stringify([{ identifier, content }]));
            }
          }
        }
      } catch (e) {
        return undefined;
      }
    },
    get: (identifier: string): DataTableSortContent | undefined => {
      try {
        const key = identifierToDataTableKey(identifier);
        if (key == null) return undefined;

        const sortContentsAry = StorageUtil.getAsBase64(key);
        if (sortContentsAry) {
          let sortContent = JSON.parse(sortContentsAry).find((e: any) => e['identifier'] == identifier);
          if (sortContent) {
            return sortContent['content'];
          }
        }
        return undefined;
      } catch (e) {
        return undefined;
      }
    },
  },
  selectedPlanId: {
    set: (id: number) => {
      StorageUtil.set(keys.selected_plan_id, `${id}`);
    },
    get: () => {
      const id = StorageUtil.get(keys.selected_plan_id);
      return id != null ? Number(id) : null;
    },
    clear: () => {
      StorageUtil.clear(keys.selected_plan_id);
    },
  },
};
